/**
 * Error translation utility for API responses.
 *
 * Translates backend error codes to localized user messages.
 * All error codes map to keys in the i18n locale files under the "errors" namespace.
 */

import { t } from '@/lib/i18n';

export interface ApiErrorDetails {
  field?: string;
  attemptsRemaining?: number;
  minValue?: number;
  maxValue?: number;
  allowedValues?: string[];
  resourceType?: string;
  resourceId?: string;
  currentState?: string;
  validTransitions?: string[];
  sku?: string;
}

export interface ApiErrorResponse {
  success: false;
  errorCode: string;
  errorDetails?: ApiErrorDetails;
}

/**
 * Translate an error code to a localized message.
 *
 * @param errorCode - Machine-readable error code from backend
 * @param errorDetails - Optional additional context for interpolation
 * @returns Localized error message
 *
 * @example
 * ```tsx
 * if (!result.success) {
 *   const message = translateError(result.errorCode, result.errorDetails);
 *   toast.error(message);
 * }
 * ```
 */
export function translateError(
  errorCode: string,
  errorDetails?: ApiErrorDetails
): string {
  // Build interpolation values from details
  const values: Record<string, string | number> = {};

  if (errorDetails?.field) {
    // Try to translate the field name, fallback to raw value
    const fieldKey = `fields.${errorDetails.field}`;
    const translatedField = t(fieldKey);
    values.field = translatedField !== fieldKey ? translatedField : errorDetails.field;
  }

  if (errorDetails?.attemptsRemaining !== undefined) {
    values.count = errorDetails.attemptsRemaining;
  }

  if (errorDetails?.minValue !== undefined) {
    values.min = errorDetails.minValue;
  }

  if (errorDetails?.maxValue !== undefined) {
    values.max = errorDetails.maxValue;
  }

  if (errorDetails?.allowedValues) {
    values.allowed = errorDetails.allowedValues.join(', ');
  }

  if (errorDetails?.resourceType) {
    // Try to translate the resource type, fallback to raw value
    const resourceKey = `resources.${errorDetails.resourceType}`;
    const translatedResource = t(resourceKey);
    values.resourceType = translatedResource !== resourceKey ? translatedResource : errorDetails.resourceType;
  }

  if (errorDetails?.currentState) {
    values.currentState = errorDetails.currentState;
  }

  if (errorDetails?.validTransitions) {
    values.validTransitions = errorDetails.validTransitions.join(', ');
  }

  if (errorDetails?.sku) {
    values.sku = errorDetails.sku;
  }

  // Try to get the translated message
  const key = `errors.${errorCode}`;
  const message = t(key, values);

  // If translation not found (returns the key), fall back to generic error
  if (message === key) {
    return t('errors.SYSTEM_ERROR');
  }

  return message;
}

/**
 * Get translated message, handling both old (message) and new (errorCode) formats.
 * Use this during migration period when some endpoints return message strings.
 */
export function getApiErrorMessage(response: {
  success?: boolean;
  message?: string;
  errorCode?: string;
  errorDetails?: ApiErrorDetails;
}): string {
  if (response.errorCode) {
    return translateError(response.errorCode, response.errorDetails);
  }
  if (response.message) {
    const normalizedMessage = response.message.replace(/^\[GraphQL\]\s*/i, '').trim();
    if (/^[A-Z][A-Z0-9_]+$/.test(normalizedMessage)) {
      return translateError(normalizedMessage, response.errorDetails);
    }
    // Some thrown errors carry a single dynamic value the client-side error
    // codes have no structured `errorDetails` channel for (e.g. a raw
    // GraphQL exception has no `errorDetails` payload at all). These use
    // the "CODE: value" convention — recognized here so the value still
    // reaches translation interpolation instead of showing the raw code.
    const withDetail = normalizedMessage.match(/^([A-Z][A-Z0-9_]+):\s*(.+)$/);
    if (withDetail) {
      const [, code, detail] = withDetail;
      return translateError(code, { ...response.errorDetails, sku: detail });
    }
    // Raw GraphQL/provider text may be English or reveal internal details.
    // Only recognized error codes may become merchant-visible copy.
    return t('errors.SYSTEM_ERROR');
  }
  return t('errors.SYSTEM_ERROR');
}

/**
 * Hook for handling API errors with automatic translation.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { handleApiError, getErrorMessage } = useApiError();
 *
 *   const onSubmit = async () => {
 *     const result = await mutation();
 *     if (!result.success) {
 *       const message = getErrorMessage(result);
 *       toast.error(message);
 *     }
 *   };
 * }
 * ```
 */
export function useApiError() {
  /**
   * Get translated error message from API error response.
   */
  const handleApiError = (response: ApiErrorResponse): string => {
    return translateError(response.errorCode, response.errorDetails);
  };
  return {
    handleApiError,
    getErrorMessage: getApiErrorMessage,
    translateError,
  };
}
